/**
 * Global SEO Configuration for Punto Hispano
 * Centralized configuration for canonical URLs, titles, meta descriptions
 */

// Base URL for canonical URLs
export const SEO_BASE_URL = "https://puntohispano.ch";

// Branded OG images (1200x630) hosted on Lovable CDN.
// Absolute HTTPS URLs are required by social crawlers (LinkedIn, Facebook, WhatsApp, Slack, Twitter/X).
export const OG_IMAGES = {
  home: `${SEO_BASE_URL}/__l5e/assets-v1/879a2aa2-e115-47ce-ab94-8dbfc5f5860f/og-home.jpg`,
  gestoria: `${SEO_BASE_URL}/__l5e/assets-v1/34c4f65e-d1b2-44b8-9d9b-f9cd683461c7/og-gestoria.jpg`,
  legal: `${SEO_BASE_URL}/__l5e/assets-v1/d76f327e-4958-4a5a-b0ae-024ad108d1e4/og-legal.jpg`,
  seguros: `${SEO_BASE_URL}/__l5e/assets-v1/ca824b3a-0b03-4c73-aff8-8ec8e537b5d2/og-seguros.jpg`,
  digital: `${SEO_BASE_URL}/__l5e/assets-v1/64055ffd-b8ef-44e6-9d28-c5daf75db5c8/og-digital.jpg`,
} as const;

/**
 * Selects the correct branded OG image for a given route.
 * Falls back to the sitewide "home" variant so no page ships without a card.
 * Locale prefixes (/en/, /de/, /fr/, /it/, /pt/) are stripped before matching.
 */
export const getOgImageForPath = (path: string): string => {
  const cleanPath = path.replace(/^\/(en|de|fr|it|pt)(?=\/|$)/, "") || "/";
  if (/\/gestoria|\/servicios\/gestoria/.test(cleanPath)) return OG_IMAGES.gestoria;
  if (/\/legal|\/asesoria-legal/.test(cleanPath)) return OG_IMAGES.legal;
  if (/\/seguros|\/consultoria-seguros|\/tercer-pilar/.test(cleanPath)) return OG_IMAGES.seguros;
  if (/\/digital|\/soluciones-digitales|\/emprende/.test(cleanPath)) return OG_IMAGES.digital;
  return OG_IMAGES.home;
};

// Default SEO fallbacks
export const SEO_DEFAULTS = {
  title: "Punto Hispano | Gestoría y Asesoría en Suiza",
  description: "Trámites y asesoría legal/administrativa en Suiza. RAV, permisos, empresas, impuestos, vivienda, trabajo, seguros, idiomas y digital.",
  keywords: "gestoría suiza, asesoría legal suiza, trámites suiza, hispanohablantes suiza, RAV, permisos suiza",
  ogImage: OG_IMAGES.home,
  ogType: "website" as const,
  locale: "es_ES",
  siteName: "Punto Hispano"
};

// Page-specific SEO configurations
export const PAGE_SEO_CONFIG: Record<string, {
  title: string;
  description: string;
  canonical?: string;
  keywords?: string;
  ogType?: "website" | "article";
  noindex?: boolean;
}> = {
  // HOME PAGE
  "/": {
    title: "Gestoría y Asesoría en Suiza | Punto Hispano",
    description: "Gestoría, asesoría legal, seguros y trámites en Suiza para hispanohablantes. Abogados y gestores en español en Zurich. +10 años de experiencia.",
    canonical: `${SEO_BASE_URL}/`,
    keywords: "gestoria hispano suiza, gestoría zurich, abogados españoles en suiza, asesoría legal zurich, gestoria fiscal, abogado español en suiza, gestoria cerca de mi, tax help near me, abogados suiza, consultoría de seguros, steuerberatung zürich, gestoria hispanohablantes, asesoría legal española, trámites suiza en español, RAV en español, permisos residencia suiza, hispanohablantes zurich, gestoria fiscal zurich, mudanza suiza, relocation switzerland",
    ogType: "website"
  },

  // SERVICIOS INDEX
  "/servicios": {
    title: "Servicios para Hispanohablantes en Suiza | PH",
    description: "Gestoría, asesoría legal y fiscal, relocation, seguros, idiomas y soluciones digitales para hispanohablantes en Suiza. Primera consulta gratuita.",
    canonical: `${SEO_BASE_URL}/servicios`,
    keywords: "servicios gestoría suiza, asesoría legal suiza, trámites administrativos, pack relocation, seguros suiza, cursos alemán, soluciones digitales, gestoría fiscal zurich, hispanohablantes suiza"
  },

  // GESTORÍA & ADMINISTRACIÓN
  "/servicios/gestoria-administracion": {
    title: "Gestoría en Suiza para Hispanohablantes | PH",
    description: "RAV, permisos, documentación, autónomos y contabilidad. Gestión ágil en Zúrich y toda Suiza, en español. Primera consulta gratuita.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion`,
    keywords: "gestoría suiza, RAV, permisos suiza, trámites administrativos suiza, gestoria administrativa zurich, documentación oficial, empadronamiento, crear empresa suiza, contabilidad suiza, hispanohablantes"
  },

  // RAV
  "/servicios/gestoria-administracion/rav": {
    title: "Gestión del RAV en Suiza: registro y prestaciones",
    description: "Inscripción en RAV, preparación de documentos y citas. Acompañamiento en español para agilizar tus prestaciones.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/rav`,
    keywords: "RAV suiza, paro suiza, seguro desempleo suiza, prestaciones RAV"
  },

  // TRADUCCIONES
  "/servicios/gestoria-administracion/traducciones": {
    title: "Traducciones juradas en Suiza (ES–DE–EN)",
    description: "Traducciones certificadas y apostillas para estudios, trabajo y trámites oficiales. Entrega rápida válida en Suiza.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/traducciones`,
    keywords: "traducciones juradas suiza, apostilla suiza, traducción certificada"
  },

  // PERMISOS Y DOCUMENTACIÓN
  "/servicios/gestoria-administracion/permisos-documentacion": {
    title: "Permisos y documentación en Suiza",
    description: "Residencia, empadronamiento, certificados y convalidaciones. Tramitamos todo por ti sin complicaciones.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/permisos-documentacion`,
    keywords: "permisos residencia suiza, empadronamiento suiza, documentación oficial suiza"
  },

  // AUTÓNOMOS
  "/servicios/gestoria-administracion/autonomos": {
    title: "Darse de alta como autónomo en Suiza",
    description: "Requisitos, alta, seguros y fiscalidad para trabajadores por cuenta propia. Te guiamos paso a paso.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/autonomos`,
    keywords: "autónomo suiza, trabajador independiente suiza, alta autónomos suiza"
  },

  // EMPRESAS
  "/servicios/gestoria-administracion/empresas": {
    title: "Constitución de empresas en Suiza",
    description: "Forma jurídica, estatutos, cuenta bancaria y registro mercantil. Acompañamiento completo hasta operar.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/empresas`,
    keywords: "crear empresa suiza, constitución empresas suiza, registro mercantil suiza"
  },

  // CONTABILIDAD
  "/servicios/gestoria-administracion/contabilidad": {
    title: "Contabilidad y nóminas para pymes en Suiza",
    description: "Contabilidad, salarios, IVA y reportes claros para tu negocio en Suiza. Planes a medida.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/contabilidad`,
    keywords: "contabilidad suiza, nóminas suiza, IVA suiza, contable suiza"
  },

  // ASESORÍA LEGAL
  "/servicios/asesoria-legal": {
    title: "Asesoría Legal en Suiza en Español | PH",
    description: "Abogados en derecho laboral, civil, migratorio y contratos en Suiza. Orientación clara en español para resolver tu caso legal.",
    canonical: `${SEO_BASE_URL}/servicios/asesoria-legal`,
    keywords: "asesoría legal suiza, abogado español suiza, derecho laboral suiza, abogados hispanohablantes, asesoría migratoria, contratos trabajo, despidos suiza, reagrupación familiar, derecho civil suiza"
  },

  // LABORAL
  "/servicios/asesoria-legal/laboral": {
    title: "Asesoría laboral en Suiza",
    description: "Despidos, contratos, salarios y acoso laboral. Revisión documental y estrategia legal efectiva.",
    canonical: `${SEO_BASE_URL}/servicios/asesoria-legal/laboral`,
    keywords: "asesoría laboral suiza, derecho trabajo suiza, despido suiza, contratos trabajo"
  },

  // MIGRATORIA
  "/servicios/asesoria-legal/migratoria": {
    title: "Asesoría migratoria en Suiza",
    description: "Permisos, reagrupación familiar y recursos. Te guiamos con plazos y requisitos para evitar rechazos.",
    canonical: `${SEO_BASE_URL}/servicios/asesoria-legal/migratoria`,
    keywords: "asesoría migratoria suiza, reagrupación familiar suiza, permisos residencia"
  },

  // FISCAL / IMPUESTOS
  "/servicios/asesoria-legal/fiscal-impuestos": {
    title: "Asesoría fiscal e impuestos en Suiza",
    description: "Declaración, optimización y regularización fiscal. Servicio en español con enfoque práctico.",
    canonical: `${SEO_BASE_URL}/servicios/asesoria-legal/fiscal-impuestos`,
    keywords: "asesoría fiscal suiza, impuestos suiza, declaración fiscal suiza"
  },

  // CURSOS DE IDIOMAS
  "/servicios/cursos-idiomas": {
    title: "Cursos de Alemán e Inglés en Suiza | PH",
    description: "Aprende alemán e inglés con profesores nativos en Zurich. Cursos presenciales y online para integrarte en Suiza. Prueba gratuita disponible.",
    canonical: `${SEO_BASE_URL}/servicios/cursos-idiomas`,
    keywords: "cursos alemán suiza, cursos inglés suiza, idiomas suiza, aprender alemán zurich, clases alemán, alemán para trabajar, cursos idiomas hispanohablantes, alemán nivel A1 A2 B1 B2, inglés business, integración suiza"
  },

  // SEGUROS
  "/servicios/consultoria-seguros": {
    title: "Consultoría de Seguros en Suiza | PH",
    description: "Seguros obligatorios: médico, responsabilidad civil, hogar y vehículos. Comparamos precios y te ayudamos a elegir la mejor opción.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros`,
    keywords: "seguros suiza, seguro médico suiza, seguros obligatorios suiza, responsabilidad civil, seguro hogar, consultor seguros, comparar seguros, KVG, seguro accidentes, tercer pilar, seguros hispanohablantes"
  },

  // SOLUCIONES DIGITALES
  "/servicios/soluciones-digitales-avanzadas": {
    title: "Soluciones Digitales para Empresas | PH",
    description: "Desarrollo web, SEO, automatizaciones y analítica digital. Transforma tu negocio con tecnología avanzada. Equipo especializado en Zurich.",
    canonical: `${SEO_BASE_URL}/servicios/soluciones-digitales-avanzadas`,
    keywords: "desarrollo web suiza, SEO suiza, automatización negocios, marketing digital, páginas web empresas, diseño web zurich, ecommerce suiza, CRM automatización, inteligencia artificial, transformación digital"
  },

  // ALQUILERES VEHÍCULOS
  "/servicios/alquileres-vehiculos": {
    title: "Alquiler de Vehículos en Suiza | PH",
    description: "Alquiler de coches flexible y transparente en Suiza. Tarifas competitivas, seguros incluidos y asesoramiento en español.",
    canonical: `${SEO_BASE_URL}/servicios/alquileres-vehiculos`,
    keywords: "alquiler coches suiza, vehículos alquiler suiza, rent car suiza, alquiler autos zurich, coches alquiler temporal, vehículos hispanohablantes, car rental switzerland"
  },

  // ALQUILERES VIVIENDAS
  "/servicios/alquileres-viviendas": {
    title: "Pisos y Habitaciones en Suiza | PH",
    description: "Habitaciones y apartamentos en alquiler en Zurich y ciudades principales. Contratos claros, ubicaciones estratégicas y soporte en español.",
    canonical: `${SEO_BASE_URL}/servicios/alquileres-viviendas`,
    keywords: "alquiler pisos suiza, habitaciones suiza, vivienda suiza, apartamentos zurich, alquiler temporal, pisos hispanohablantes, vivienda estudiantes, apartamentos amueblados"
  },

  "/servicios/cursos-idiomas/aleman": {
    title: "Cursos de Alemán en Suiza | A1 a C2 | PH",
    description: "Aprende alemán suizo con profesores nativos. Cursos presenciales y online desde nivel A1 hasta C2. Preparación para exámenes oficiales.",
    canonical: `${SEO_BASE_URL}/servicios/cursos-idiomas/aleman`,
    keywords: "cursos alemán suiza, alemán suizo, clases alemán zurich, alemán A1 A2 B1 B2 C1 C2, profesores nativos, exámenes alemán, Goethe Institut"
  },

  "/servicios/cursos-idiomas/ingles": {
    title: "Cursos de Inglés Business en Suiza | PH",  
    description: "Inglés profesional y business en Zurich. Mejora tus oportunidades laborales con cursos especializados para el entorno empresarial suizo.",
    canonical: `${SEO_BASE_URL}/servicios/cursos-idiomas/ingles`,
    keywords: "cursos inglés suiza, inglés business, inglés profesional, cursos inglés empresas, certificaciones inglés, TOEFL IELTS, inglés corporativo"
  },

  "/servicios/seguros/seguros-personales": {
    title: "Seguros Personales en Suiza | Punto Hispano",
    description: "Seguros obligatorios y opcionales: médico (KVG), responsabilidad civil, hogar, accidentes. Comparamos y te ayudamos a elegir la mejor opción.",
    canonical: `${SEO_BASE_URL}/servicios/seguros/seguros-personales`,
    keywords: "seguros personales suiza, seguro médico KVG, responsabilidad civil, seguro hogar, seguro accidentes, seguros obligatorios"
  },

  "/servicios/seguros/seguros-empresariales": {
    title: "Seguros Empresariales en Suiza | Punto Hispano",
    description: "Protege tu empresa con los seguros adecuados: responsabilidad civil empresarial, seguros de empleados, protección jurídica y más.",
    canonical: `${SEO_BASE_URL}/servicios/seguros/seguros-empresariales`, 
    keywords: "seguros empresariales suiza, responsabilidad civil empresa, seguros empleados, protección jurídica empresas, seguros comerciales"
  },

  "/servicios/seguros/tercer-pilar": {
    title: "Tercer Pilar 3a en Suiza | Ahorro | PH",
    description: "Optimiza tu jubilación con el Tercer Pilar 3a. Te ayudamos a elegir la mejor opción de ahorro y planificar tu futuro financiero en Suiza.",
    canonical: `${SEO_BASE_URL}/servicios/seguros/tercer-pilar`,
    keywords: "tercer pilar 3a suiza, ahorro jubilación, pensiones suiza, pilar 3a, planificación financiera, ahorro fiscal, jubilación suiza"
  },
  "/servicios/pack-relocation": {
    title: "Pack Relocation Suiza | Mudanza Integral | PH",
    description: "Relocation integral: recogida en aeropuerto, empadronamiento, cuenta bancaria, seguro médico, SIM y más. Llega a Suiza sin preocupaciones.",
    canonical: `${SEO_BASE_URL}/servicios/pack-relocation`,
    keywords: "pack relocation suiza, mudanza suiza, asentamiento suiza, llegada suiza, servicio integral mudanza, relocation services switzerland, mudanza zurich, expatriados suiza"
  },

  // BLOG
  "/blog": {
    title: "Blog | Guías para Vivir en Suiza | PH",
    description: "Consejos expertos y guías: permisos, impuestos, vivienda, empleo, seguros e idiomas para hispanohablantes en Suiza. Artículos actualizados.",
    canonical: `${SEO_BASE_URL}/blog`,
    keywords: "blog suiza, guías suiza, consejos vivir suiza, trabajar suiza, expatriados españa, mudanza suiza, trámites suiza, hispanohablantes suiza, guías gratuitas"
  },

  // QUIÉNES SOMOS
  "/quienes-somos": {
    title: "Quiénes Somos | Punto Hispano Suiza",
    description: "Equipo de profesionales hispanohablantes en Zurich con más de 10 años de experiencia. Conocemos los trámites suizos por experiencia propia.",
    canonical: `${SEO_BASE_URL}/quienes-somos`,
    keywords: "equipo punto hispano, gestoría española suiza, asesoría hispanohablantes, profesionales españoles zurich, experiencia trámites suiza, equipo bilingüe"
  },

  // CONTACTO
  "/contacto": {
    title: "Contacto | Punto Hispano Suiza",
    description: "Contáctanos por WhatsApp, email o reserva cita en Zurich. Primera consulta gratuita. Respuesta rápida garantizada en español.",
    canonical: `${SEO_BASE_URL}/contacto`,
    keywords: "contacto punto hispano, gestoría zúrich, asesoría español suiza, consulta gratuita, oficina zurich, whatsapp gestoría, cita presencial"
  },

  // RECURSOS
  "/recursos": {
    title: "Recursos para vivir y trabajar en Suiza | Punto Hispano",
    description: "Guías, vídeos y materiales descargables para tus trámites en Suiza.",
    canonical: `${SEO_BASE_URL}/recursos`,
    keywords: "recursos suiza, guías gratuitas suiza, materiales trámites"
  },

  // FAQ
  "/faq": {
    title: "Preguntas frecuentes sobre trámites en Suiza | Punto Hispano",
    description: "Respuestas claras sobre permisos, impuestos, vivienda, empleo, seguros e idiomas.",
    canonical: `${SEO_BASE_URL}/faq`,
    keywords: "FAQ suiza, preguntas frecuentes suiza, dudas trámites suiza"
  },

  // VIVIR EN SUIZA
  "/vivir-en-suiza": {
    title: "Guía completa para vivir en Suiza | Punto Hispano",
    description: "Todo lo que necesitas saber para vivir en Suiza: trámites, cultura, trabajo y consejos prácticos.",
    canonical: `${SEO_BASE_URL}/vivir-en-suiza`,
    keywords: "vivir suiza, guía suiza, mudarse suiza, expatriados suiza"
  },

  // RESIDENTES EN SUIZA
  "/residentes-en-suiza": {
    title: "Servicios para residentes en Suiza | Punto Hispano",
    description: "Servicios especializados para hispanohablantes ya establecidos en Suiza.",
    canonical: `${SEO_BASE_URL}/residentes-en-suiza`,
    keywords: "residentes suiza, servicios residentes, hispanohablantes establecidos"
  },

  // DIGITAL
  "/digital": {
    title: "Transformación Digital en Suiza | Punto Hispano",
    description: "Soluciones digitales avanzadas para empresas en Suiza: web, SEO, automatización y más.",
    canonical: `${SEO_BASE_URL}/digital`,
    keywords: "transformación digital suiza, desarrollo web suiza, automatización empresas"
  },

  // UTILITY PAGES - NOINDEX
  "/coming-soon": {
    title: "Próximamente | Punto Hispano",
    description: "Punto Hispano está preparando nuevos servicios para hispanohablantes en Suiza. Vuelve pronto para descubrir nuestras novedades.",
    canonical: `${SEO_BASE_URL}/coming-soon`,
    noindex: true
  },
  "/gracias": {
    title: "Gracias por contactar | Punto Hispano",
    description: "Hemos recibido tu mensaje. Nuestro equipo en Zurich te responderá en menos de 24 horas para coordinar tu consulta personalizada en español.",
    canonical: `${SEO_BASE_URL}/gracias`,
    noindex: true
  },
  "/pago-exitoso": {
    title: "Pago confirmado | Punto Hispano",
    description: "Tu pago se ha procesado correctamente. En breve recibirás un correo con la confirmación, los detalles de tu servicio y los próximos pasos.",
    canonical: `${SEO_BASE_URL}/pago-exitoso`,
    noindex: true
  },
  "/experiencia": {
    title: "Experiencia Inmersiva 3D | Punto Hispano Suiza",
    description: "Recorre virtualmente la oficina de Punto Hispano en Zurich: gestoría, asesoría legal, seguros e idiomas explicados en una experiencia 3D interactiva.",
    canonical: `${SEO_BASE_URL}/experiencia`,
    keywords: "experiencia inmersiva punto hispano, oficina virtual zurich, recorrido 3d servicios suiza, gestoría interactiva hispanohablantes"
  },
  "/error": {
    title: "Error | Punto Hispano",
    description: "Ha ocurrido un error. Por favor intente nuevamente.",
    canonical: `${SEO_BASE_URL}/error`,
    noindex: true
  },

  // ZONAS
  "/zonas": {
    title: "Zonas de Servicio | Punto Hispano",
    description: "Gestoría, asesoría legal, seguros y más en todo el mundo. Atención online desde nuestra oficina en Opfikon, Suiza.",
    canonical: `${SEO_BASE_URL}/zonas`,
    keywords: "zonas servicio, atención global, hispanohablantes mundo, gestoría internacional"
  },

  // EMPRENDE
  "/emprende": {
    title: "Emprende | Soluciones Digitales | PH",
    description: "Impulsa tu negocio digital: desarrollo web, CRM, IA, automatización y marketing. Cuéntanos tu idea y la hacemos realidad.",
    canonical: `${SEO_BASE_URL}/emprende`,
    keywords: "emprendimiento digital, desarrollo web, soluciones digitales, startup, automatización, IA"
  },
  "/emprende/ideas": {
    title: "Transmite Tus Ideas | Proyectos | PH",
    description: "¿Tienes una idea de negocio digital? Cuéntanos tu proyecto y te ayudamos a desarrollarlo con tecnología moderna.",
    canonical: `${SEO_BASE_URL}/emprende/ideas`,
    keywords: "ideas negocio, proyectos digitales, desarrollo startup, consultoría tecnológica"
  },
  "/emprende/gana-dinero": {
    title: "Gana Dinero Online | Guías | PH",
    description: "Aprende a monetizar tus habilidades online. Freelancing, startups, ingresos pasivos y más.",
    canonical: `${SEO_BASE_URL}/emprende/gana-dinero`,
    keywords: "ganar dinero online, freelancing, monetización, ingresos pasivos, trabajo remoto"
  },
  "/emprende/soluciones-digitales": {
    title: "Soluciones Digitales | Web, CRM, IA | PH",
    description: "Catálogo completo de servicios digitales: desarrollo web, CRM, chatbots IA, automatización, marketing digital.",
    canonical: `${SEO_BASE_URL}/emprende/soluciones-digitales`,
    keywords: "soluciones digitales, desarrollo web, CRM, inteligencia artificial, automatización"
  },
  "/emprende/colabora": {
    title: "Colabora con Nosotros | Partners | PH",
    description: "Únete a nuestro programa de colaboradores. Beneficios exclusivos para partners tecnológicos y afiliados.",
    canonical: `${SEO_BASE_URL}/emprende/colabora`,
    keywords: "partners, colaboradores, afiliados, programa partners, tecnología"
  },

  // GESTORÍA SUBPAGES
  "/servicios/gestoria-administracion/desempleo": {
    title: "Gestión del Desempleo en Suiza | PH",
    description: "Inscripción RAV, prestaciones y búsqueda de empleo. Te acompañamos en todo el proceso en español para que cobres sin retrasos.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/desempleo`,
    keywords: "desempleo suiza, RAV, paro suiza, prestaciones desempleo, seguro desempleo suiza"
  },
  "/servicios/gestoria-administracion/administracion": {
    title: "Trámites Administrativos en Suiza | PH",
    description: "Empadronamiento, certificados, convalidaciones y documentación oficial. Gestión ágil en español sin complicaciones.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/administracion`,
    keywords: "trámites administrativos suiza, empadronamiento suiza, certificados suiza, documentación"
  },
  "/servicios/gestoria-administracion/cv": {
    title: "CV y Documentación Profesional | PH",
    description: "Preparación de CV suizo, cartas de presentación y documentación laboral adaptada al mercado suizo.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/cv`,
    keywords: "CV suiza, currículum suizo, documentación laboral, carta presentación, búsqueda empleo suiza"
  },
  "/servicios/gestoria-administracion/acompanamientos": {
    title: "Acompañamientos Presenciales en Suiza | PH",
    description: "Te acompañamos a citas oficiales, bancos y oficinas públicas. Intérprete y asesor en español a tu lado.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/acompanamientos`,
    keywords: "acompañamiento citas suiza, intérprete suiza, ayuda trámites presencial, oficinas públicas"
  },
  "/servicios/gestoria-administracion/paquetes": {
    title: "Paquetes de Gestoría en Suiza | PH",
    description: "Paquetes completos de gestión administrativa con descuento. Elige el plan que mejor se adapte a tu situación.",
    canonical: `${SEO_BASE_URL}/servicios/gestoria-administracion/paquetes`,
    keywords: "paquetes gestoría, planes gestoría suiza, servicios combinados, descuentos trámites"
  },

  // LEGAL SUBPAGES
  "/servicios/asesoria-legal/extranjeria": {
    title: "Derecho de Extranjería en Suiza | PH",
    description: "Permisos de residencia, reagrupación familiar y recursos migratorios. Abogados en español especializados.",
    canonical: `${SEO_BASE_URL}/servicios/asesoria-legal/extranjeria`,
    keywords: "extranjería suiza, permisos residencia, reagrupación familiar, abogado extranjería suiza"
  },
  "/servicios/asesoria-legal/familia": {
    title: "Derecho de Familia en Suiza | PH",
    description: "Divorcios, custodia, pensiones alimenticias y adopción. Asesoría legal familiar en español.",
    canonical: `${SEO_BASE_URL}/servicios/asesoria-legal/familia`,
    keywords: "derecho familia suiza, divorcio suiza, custodia hijos, pensión alimenticia suiza"
  },
  "/servicios/asesoria-legal/mercantil": {
    title: "Derecho Mercantil en Suiza | PH",
    description: "Constitución de empresas, contratos comerciales y litigios mercantiles. Asesoramiento jurídico empresarial.",
    canonical: `${SEO_BASE_URL}/servicios/asesoria-legal/mercantil`,
    keywords: "derecho mercantil suiza, contratos comerciales, constitución empresa, litigios comerciales"
  },
  "/servicios/asesoria-legal/inmobiliario": {
    title: "Derecho Inmobiliario en Suiza | PH",
    description: "Contratos de alquiler, compraventa y disputas inmobiliarias. Protección legal para inquilinos y propietarios.",
    canonical: `${SEO_BASE_URL}/servicios/asesoria-legal/inmobiliario`,
    keywords: "derecho inmobiliario suiza, contrato alquiler, compraventa inmueble, disputas alquiler suiza"
  },

  // SEGUROS SUBPAGES
  "/servicios/consultoria-seguros/salud": {
    title: "Seguro de Salud en Suiza (KVG) | PH",
    description: "Compara seguros médicos obligatorios. Te ayudamos a elegir la mejor caja y reducir tu prima mensual.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/salud`,
    keywords: "seguro salud suiza, KVG, seguro médico obligatorio, cajas enfermedad, comparar seguros médicos"
  },
  "/servicios/consultoria-seguros/hogar": {
    title: "Seguro de Hogar en Suiza | PH",
    description: "Protege tu vivienda y enseres con el seguro adecuado. Comparamos opciones y precios para ti.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/hogar`,
    keywords: "seguro hogar suiza, seguro casa, seguro contenido, protección vivienda suiza"
  },
  "/servicios/consultoria-seguros/vehiculos": {
    title: "Seguro de Vehículos en Suiza | PH",
    description: "Seguro obligatorio y a todo riesgo para coches y motos. Compara precios y ahorra en tu póliza.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/vehiculos`,
    keywords: "seguro coche suiza, seguro vehículo, seguro moto, seguro auto obligatorio suiza"
  },
  "/servicios/consultoria-seguros/vida-pension": {
    title: "Seguro de Vida y Pensión en Suiza | PH",
    description: "Planifica tu jubilación y protege a tu familia. Tercer pilar 3a, seguros de vida y pensiones.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/vida-pension`,
    keywords: "seguro vida suiza, pensión suiza, tercer pilar, jubilación, planificación financiera"
  },
  "/servicios/consultoria-seguros/empresariales": {
    title: "Seguros Empresariales en Suiza | PH",
    description: "Responsabilidad civil, seguros para empleados y protección jurídica empresarial. Soluciones a medida.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/empresariales`,
    keywords: "seguros empresa suiza, responsabilidad civil empresarial, seguros empleados, BVG"
  },
  "/servicios/consultoria-seguros/accidentes-salud": {
    title: "Seguro de Accidentes y Salud en Suiza | PH",
    description: "Cobertura de accidentes laborales y no laborales. Complementos de salud para mayor protección.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/accidentes-salud`,
    keywords: "seguro accidentes suiza, UVG, accidentes laborales, complemento salud"
  },
  "/servicios/consultoria-seguros/viaje-transporte": {
    title: "Seguro de Viaje y Transporte en Suiza | PH",
    description: "Seguros para viajes internacionales y transporte de mercancías. Viaja protegido desde Suiza.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/viaje-transporte`,
    keywords: "seguro viaje suiza, seguro transporte, viaje internacional, cobertura viajero"
  },
  "/servicios/consultoria-seguros/comparador": {
    title: "Comparador de Seguros en Suiza | PH",
    description: "Compara seguros médicos, hogar, auto y vida. Encuentra la mejor opción al mejor precio en minutos.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/comparador`,
    keywords: "comparador seguros suiza, comparar precios seguros, mejor seguro suiza"
  },
  "/servicios/consultoria-seguros/siniestros": {
    title: "Gestión de Siniestros en Suiza | PH",
    description: "Te ayudamos con la declaración y seguimiento de siniestros. Reclamaciones en español sin estrés.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/siniestros`,
    keywords: "siniestros suiza, reclamación seguro, declaración siniestro, gestión seguros"
  },
  "/servicios/consultoria-seguros/beneficios-sociales": {
    title: "Beneficios Sociales en Suiza | PH",
    description: "Ayudas sociales, subsidios y prestaciones disponibles en Suiza. Te orientamos sobre tus derechos.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/beneficios-sociales`,
    keywords: "beneficios sociales suiza, ayudas sociales, subsidios suiza, prestaciones sociales"
  },
  "/servicios/consultoria-seguros/formacion": {
    title: "Formación en Seguros en Suiza | PH",
    description: "Talleres y guías para entender el sistema de seguros suizo. Aprende a optimizar tus coberturas.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/formacion`,
    keywords: "formación seguros suiza, talleres seguros, sistema seguros suizo"
  },
  "/servicios/consultoria-seguros/asignacion-hijos": {
    title: "Asignación Familiar por Hijos en Suiza | PH",
    description: "Solicita las prestaciones por hijos: Kinderzulage y Ausbildungszulage. Te ayudamos con el trámite.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/asignacion-hijos`,
    keywords: "asignación hijos suiza, Kinderzulage, prestación familiar, ayuda hijos suiza"
  },
  "/servicios/consultoria-seguros/reduccion-prima": {
    title: "Reducción de Prima de Seguro Médico | PH",
    description: "Solicita la reducción de prima (IPV/Prämienverbilligung) y ahorra en tu seguro de salud.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/reduccion-prima`,
    keywords: "reducción prima suiza, IPV, Prämienverbilligung, ahorro seguro médico"
  },
  "/servicios/consultoria-seguros/baja-laboral": {
    title: "Baja Laboral y Enfermedad en Suiza | PH",
    description: "Derechos y trámites durante una baja laboral. Seguro de pérdida de ingresos y protección.",
    canonical: `${SEO_BASE_URL}/servicios/consultoria-seguros/baja-laboral`,
    keywords: "baja laboral suiza, enfermedad trabajo, pérdida ingresos, Krankentaggeld"
  },

  // RELOCATION SUBPAGES
  "/servicios/pack-relocation/basico": {
    title: "Pack Relocation Básico | Mudanza Suiza | PH",
    description: "Lo esencial para llegar a Suiza: empadronamiento, cuenta bancaria y seguro médico. Precio accesible.",
    canonical: `${SEO_BASE_URL}/servicios/pack-relocation/basico`,
    keywords: "relocation básico suiza, mudanza básica, llegada suiza, pack esencial"
  },
  "/servicios/pack-relocation/completo": {
    title: "Pack Relocation Completo | Todo Incluido | PH",
    description: "Servicio integral: aeropuerto, vivienda, trámites, seguros, SIM y acompañamiento. Llega sin preocupaciones.",
    canonical: `${SEO_BASE_URL}/servicios/pack-relocation/completo`,
    keywords: "relocation completo suiza, pack todo incluido, mudanza integral, servicio premium"
  },
  "/servicios/pack-relocation/empresas": {
    title: "Relocation Corporativo en Suiza | PH",
    description: "Reubicación de empleados y equipos. Servicio empresarial de relocation con gestión integral.",
    canonical: `${SEO_BASE_URL}/servicios/pack-relocation/empresas`,
    keywords: "relocation empresas suiza, reubicación empleados, corporate relocation switzerland"
  },
  "/servicios/pack-relocation/retorno": {
    title: "Pack Retorno | Volver a tu País desde Suiza",
    description: "Trámites de salida, desregistro, tercer pilar y documentación para volver a tu país de origen.",
    canonical: `${SEO_BASE_URL}/servicios/pack-relocation/retorno`,
    keywords: "retorno desde suiza, volver a casa, desregistro suiza, tercer pilar retorno"
  },

  // CONTACTO SUBPAGES
  "/contacto/formulario": {
    title: "Formulario de Contacto | Punto Hispano",
    description: "Escríbenos tu consulta y te respondemos en menos de 24h. Servicio en español, alemán e inglés.",
    canonical: `${SEO_BASE_URL}/contacto/formulario`,
    keywords: "contacto punto hispano, formulario consulta, gestoría zurich contacto"
  },
  "/contacto/oficinas-opfikon": {
    title: "Oficinas en Opfikon, Zürich | Punto Hispano",
    description: "Visítanos en Thurgauerstrasse 117, Opfikon. Horario de atención, cómo llegar y reserva tu cita.",
    canonical: `${SEO_BASE_URL}/contacto/oficinas-opfikon`,
    keywords: "oficinas punto hispano, Opfikon, Thurgauerstrasse, cita presencial, gestoría zürich"
  },

  // POLÍTICAS
  "/politicas": {
    title: "Políticas Legales | Punto Hispano",
    description: "Aviso legal, política de privacidad, cookies y cumplimiento GDPR de Punto Hispano.",
    canonical: `${SEO_BASE_URL}/politicas`,
    keywords: "políticas legales, aviso legal, privacidad, cookies, GDPR",
    noindex: true
  },
  "/politicas/aviso-legal": {
    title: "Aviso Legal | Punto Hispano",
    description: "Información legal, titularidad del sitio web y condiciones de uso de puntohispano.ch.",
    canonical: `${SEO_BASE_URL}/politicas/aviso-legal`,
    keywords: "aviso legal, condiciones uso, información legal",
    noindex: true
  },
  "/politicas/privacidad": {
    title: "Política de Privacidad | Punto Hispano",
    description: "Cómo recopilamos, usamos y protegemos tus datos personales conforme al RGPD y la LPD suiza.",
    canonical: `${SEO_BASE_URL}/politicas/privacidad`,
    keywords: "política privacidad, protección datos, RGPD, LPD suiza",
    noindex: true
  },
  "/politicas/cookies": {
    title: "Política de Cookies | Punto Hispano",
    description: "Tipos de cookies que utilizamos y cómo gestionarlas en tu navegador.",
    canonical: `${SEO_BASE_URL}/politicas/cookies`,
    keywords: "política cookies, cookies web, gestión cookies",
    noindex: true
  },
  "/politicas/gdpr": {
    title: "Cumplimiento GDPR | Punto Hispano",
    description: "Información sobre el cumplimiento del Reglamento General de Protección de Datos.",
    canonical: `${SEO_BASE_URL}/politicas/gdpr`,
    keywords: "GDPR, protección datos, cumplimiento normativo, derechos usuarios",
    noindex: true
  },

  // PRUEBA NIVEL
  "/prueba-nivel-ingles": {
    title: "Prueba de Nivel de Inglés Gratis | PH",
    description: "Haz nuestra prueba de nivel de inglés online y gratuita. Descubre tu nivel y elige el curso ideal.",
    canonical: `${SEO_BASE_URL}/prueba-nivel-ingles`,
    keywords: "prueba nivel inglés, test inglés gratis, nivel inglés online, examen nivel"
  },

  // SOBRE NOSOTROS
  "/sobre-nosotros": {
    title: "Sobre Nosotros | Punto Hispano Suiza",
    description: "+2.500 familias confían en nosotros. 12 años resolviendo trámites en Suiza para hispanohablantes. Conoce al equipo detrás de Punto Hispano.",
    canonical: `${SEO_BASE_URL}/sobre-nosotros`,
    keywords: "sobre punto hispano, equipo gestoría suiza, historia punto hispano, profesionales hispanohablantes zurich, Santiago Castillo, fundador"
  },

  // TRABAJA CON NOSOTROS
  "/trabaja-con-nosotros": {
    title: "Trabaja con Nosotros | Empleo | PH",
    description: "Únete al equipo de Punto Hispano. Buscamos profesionales apasionados por ayudar a la comunidad hispana en Suiza.",
    canonical: `${SEO_BASE_URL}/trabaja-con-nosotros`,
    keywords: "empleo punto hispano, trabajar gestoría suiza, ofertas empleo zurich, carreras punto hispano"
  }
};

// Multilingual SEO overrides for key pages
type LocaleSEO = { title: string; description: string; keywords?: string };
export const PAGE_SEO_I18N: Record<string, Record<string, LocaleSEO>> = {
  "/": {
    de: { title: "Verwaltung & Beratung in der Schweiz | Punto Hispano", description: "Verwaltung, Rechtsberatung, Versicherungen, Deutschkurse, Relocation und digitale Lösungen in Zürich. +10 Jahre Erfahrung.", keywords: "Verwaltung Zürich, Beratung Schweiz, Steuerberatung, Versicherungen, Deutschkurse, Relocation Schweiz" },
    en: { title: "Administration & Consulting in Switzerland | Punto Hispano", description: "Administration, legal advice, insurance, German courses, relocation and digital solutions in Zurich. +10 years of experience.", keywords: "administration Zurich, consulting Switzerland, tax help, insurance, German courses, relocation Switzerland" },
    fr: { title: "Gestion Administrative en Suisse | Punto Hispano", description: "Gestion, conseil juridique, assurances, cours d'allemand, relocation et solutions digitales à Zurich. +10 ans d'expérience.", keywords: "gestion administrative Zurich, conseil juridique Suisse, assurances, cours allemand, relocation" },
    it: { title: "Gestione Amministrativa in Svizzera | Punto Hispano", description: "Gestione, consulenza legale, assicurazioni, corsi di tedesco, relocation e soluzioni digitali a Zurigo. +10 anni di esperienza.", keywords: "gestione amministrativa Zurigo, consulenza legale Svizzera, assicurazioni, corsi tedesco, relocation" },
    pt: { title: "Gestão Administrativa na Suíça | Punto Hispano", description: "Gestão, assessoria jurídica, seguros, cursos de alemão, relocation e soluções digitais em Zurique. +10 anos de experiência.", keywords: "gestão administrativa Zurique, assessoria jurídica Suíça, seguros, cursos alemão, relocation" },
  },
  "/servicios": {
    de: { title: "Dienstleistungen in der Schweiz | PH", description: "Verwaltung, Rechtsberatung, Versicherungen, Sprachkurse, Relocation und digitale Lösungen. Erstberatung kostenlos." },
    en: { title: "Services in Switzerland | PH", description: "Administration, legal advice, insurance, language courses, relocation and digital solutions. Free first consultation." },
    fr: { title: "Services en Suisse | PH", description: "Gestion, conseil juridique, assurances, cours de langues, relocation et solutions digitales. Première consultation gratuite." },
    it: { title: "Servizi in Svizzera | PH", description: "Gestione, consulenza legale, assicurazioni, corsi di lingue, relocation e soluzioni digitali. Prima consulenza gratuita." },
    pt: { title: "Serviços na Suíça | PH", description: "Gestão, assessoria jurídica, seguros, cursos de idiomas, relocation e soluções digitais. Primeira consulta gratuita." },
  },
  "/servicios/gestoria-administracion": {
    de: { title: "Verwaltung in der Schweiz | PH", description: "RAV, Genehmigungen, Dokumente, Selbständigkeit und Buchhaltung. Effiziente Verwaltung in Zürich, auf Deutsch." },
    en: { title: "Administration Services in Switzerland | PH", description: "RAV, permits, documents, self-employment and accounting. Efficient management in Zurich." },
    fr: { title: "Gestion Administrative en Suisse | PH", description: "RAV, permis, documents, indépendants et comptabilité. Gestion efficace à Zurich." },
    it: { title: "Gestione Amministrativa in Svizzera | PH", description: "RAV, permessi, documenti, lavoro autonomo e contabilità. Gestione efficiente a Zurigo." },
    pt: { title: "Gestão Administrativa na Suíça | PH", description: "RAV, autorizações, documentos, trabalho autónomo e contabilidade. Gestão eficiente em Zurique." },
  },
  "/servicios/asesoria-legal": {
    de: { title: "Rechtsberatung in der Schweiz | PH", description: "Arbeitsrecht, Zivilrecht, Migrationsrecht und Verträge. Klare Orientierung auf Deutsch." },
    en: { title: "Legal Advice in Switzerland | PH", description: "Labor law, civil law, immigration and contracts. Clear guidance in English." },
    fr: { title: "Conseil Juridique en Suisse | PH", description: "Droit du travail, civil, migratoire et contrats. Orientation claire en français." },
    it: { title: "Consulenza Legale in Svizzera | PH", description: "Diritto del lavoro, civile, migratorio e contratti. Orientamento chiaro in italiano." },
    pt: { title: "Assessoria Jurídica na Suíça | PH", description: "Direito do trabalho, civil, migratório e contratos. Orientação clara em português." },
  },
  "/servicios/cursos-idiomas": {
    de: { title: "Sprachkurse in der Schweiz | PH", description: "Deutsch und Englisch mit Muttersprachlern in Zürich. Präsenz- und Online-Kurse. Kostenlose Probestunde." },
    en: { title: "Language Courses in Switzerland | PH", description: "German and English with native teachers in Zurich. In-person and online courses. Free trial available." },
    fr: { title: "Cours de Langues en Suisse | PH", description: "Allemand et anglais avec professeurs natifs à Zurich. Cours en présentiel et en ligne. Essai gratuit." },
    it: { title: "Corsi di Lingue in Svizzera | PH", description: "Tedesco e inglese con insegnanti madrelingua a Zurigo. Corsi in presenza e online. Prova gratuita." },
    pt: { title: "Cursos de Idiomas na Suíça | PH", description: "Alemão e inglês com professores nativos em Zurique. Cursos presenciais e online. Aula experimental grátis." },
  },
  "/servicios/consultoria-seguros": {
    de: { title: "Versicherungsberatung in der Schweiz | PH", description: "Obligatorische Versicherungen: Kranken-, Haftpflicht-, Hausrat- und Fahrzeugversicherung. Wir vergleichen Preise für Sie." },
    en: { title: "Insurance Consulting in Switzerland | PH", description: "Mandatory insurance: health, liability, home and vehicle. We compare prices and help you choose the best option." },
    fr: { title: "Conseil en Assurances en Suisse | PH", description: "Assurances obligatoires : maladie, responsabilité civile, habitation et véhicules. Nous comparons les prix pour vous." },
    it: { title: "Consulenza Assicurativa in Svizzera | PH", description: "Assicurazioni obbligatorie: sanitaria, responsabilità civile, casa e veicoli. Confrontiamo i prezzi per te." },
    pt: { title: "Consultoria de Seguros na Suíça | PH", description: "Seguros obrigatórios: saúde, responsabilidade civil, habitação e veículos. Comparamos preços para si." },
  },
  "/servicios/soluciones-digitales-avanzadas": {
    de: { title: "Digitale Lösungen für Unternehmen | PH", description: "Webentwicklung, SEO, Automatisierung und digitale Analytik. Transformieren Sie Ihr Geschäft mit moderner Technologie." },
    en: { title: "Digital Solutions for Businesses | PH", description: "Web development, SEO, automation and digital analytics. Transform your business with advanced technology." },
    fr: { title: "Solutions Digitales pour Entreprises | PH", description: "Développement web, SEO, automatisation et analytique digitale. Transformez votre entreprise avec la technologie." },
    it: { title: "Soluzioni Digitali per Aziende | PH", description: "Sviluppo web, SEO, automazione e analisi digitale. Trasforma la tua azienda con la tecnologia avanzata." },
    pt: { title: "Soluções Digitais para Empresas | PH", description: "Desenvolvimento web, SEO, automação e análise digital. Transforme o seu negócio com tecnologia avançada." },
  },
  "/servicios/pack-relocation": {
    de: { title: "Relocation-Paket Schweiz | Umzug | PH", description: "Integral Relocation: Abholung, Anmeldung, Bankkonto, Krankenversicherung, SIM und mehr. Sorgenfrei ankommen." },
    en: { title: "Relocation Pack Switzerland | Moving | PH", description: "Full relocation: airport pickup, registration, bank account, health insurance, SIM and more. Arrive worry-free." },
    fr: { title: "Pack Relocation Suisse | Déménagement | PH", description: "Relocation intégrale : accueil aéroport, inscription, compte bancaire, assurance maladie, SIM et plus." },
    it: { title: "Pacchetto Relocation Svizzera | Trasferimento | PH", description: "Relocation integrale: accoglienza aeroporto, iscrizione, conto bancario, assicurazione sanitaria, SIM e altro." },
    pt: { title: "Pacote Relocation Suíça | Mudança | PH", description: "Relocation integral: receção no aeroporto, registo, conta bancária, seguro de saúde, SIM e mais." },
  },
  "/servicios/alquileres-vehiculos": {
    de: { title: "Fahrzeugvermietung in der Schweiz | PH", description: "Flexible und transparente Autovermietung. Wettbewerbsfähige Tarife, Versicherung inklusive und Beratung auf Deutsch." },
    en: { title: "Vehicle Rental in Switzerland | PH", description: "Flexible and transparent car rental. Competitive rates, insurance included and English-speaking support." },
    fr: { title: "Location de Véhicules en Suisse | PH", description: "Location de voitures flexible et transparente. Tarifs compétitifs, assurance incluse et conseil en français." },
    it: { title: "Noleggio Veicoli in Svizzera | PH", description: "Noleggio auto flessibile e trasparente. Tariffe competitive, assicurazione inclusa e consulenza in italiano." },
    pt: { title: "Aluguer de Veículos na Suíça | PH", description: "Aluguer de carros flexível e transparente. Tarifas competitivas, seguro incluído e apoio em português." },
  },
  "/servicios/alquileres-viviendas": {
    de: { title: "Wohnungen und Zimmer in der Schweiz | PH", description: "Zimmer und Wohnungen zur Miete in Zürich. Klare Verträge, strategische Lagen und Unterstützung auf Deutsch." },
    en: { title: "Apartments and Rooms in Switzerland | PH", description: "Rooms and apartments for rent in Zurich. Clear contracts, strategic locations and English support." },
    fr: { title: "Appartements et Chambres en Suisse | PH", description: "Chambres et appartements à louer à Zurich. Contrats clairs, emplacements stratégiques et support en français." },
    it: { title: "Appartamenti e Stanze in Svizzera | PH", description: "Stanze e appartamenti in affitto a Zurigo. Contratti chiari, posizioni strategiche e supporto in italiano." },
    pt: { title: "Apartamentos e Quartos na Suíça | PH", description: "Quartos e apartamentos para alugar em Zurique. Contratos claros, localizações estratégicas e apoio em português." },
  },
  "/sobre-nosotros": {
    de: { title: "Über Uns | Punto Hispano Schweiz", description: "+2.500 Familien vertrauen uns. 12 Jahre Erfahrung in der Schweiz. Lernen Sie das Team kennen." },
    en: { title: "About Us | Punto Hispano Switzerland", description: "+2,500 families trust us. 12 years of experience in Switzerland. Meet the team behind Punto Hispano." },
    fr: { title: "À Propos | Punto Hispano Suisse", description: "+2 500 familles nous font confiance. 12 ans d'expérience en Suisse. Découvrez l'équipe." },
    it: { title: "Chi Siamo | Punto Hispano Svizzera", description: "+2.500 famiglie si fidano di noi. 12 anni di esperienza in Svizzera. Conosci il team." },
    pt: { title: "Sobre Nós | Punto Hispano Suíça", description: "+2.500 famílias confiam em nós. 12 anos de experiência na Suíça. Conheça a equipa." },
  },
  "/contacto": {
    de: { title: "Kontakt | Punto Hispano Schweiz", description: "Kontaktieren Sie uns per WhatsApp, E-Mail oder buchen Sie einen Termin. Erstberatung kostenlos. Schnelle Antwort garantiert." },
    en: { title: "Contact | Punto Hispano Switzerland", description: "Contact us via WhatsApp, email or book an appointment. Free first consultation. Fast response guaranteed." },
    fr: { title: "Contact | Punto Hispano Suisse", description: "Contactez-nous par WhatsApp, email ou prenez rendez-vous. Première consultation gratuite. Réponse rapide garantie." },
    it: { title: "Contatti | Punto Hispano Svizzera", description: "Contattaci tramite WhatsApp, email o prenota un appuntamento. Prima consulenza gratuita. Risposta rapida garantita." },
    pt: { title: "Contacto | Punto Hispano Suíça", description: "Contacte-nos por WhatsApp, email ou agende uma consulta. Primeira consulta gratuita. Resposta rápida garantida." },
  },
  "/blog": {
    de: { title: "Blog | Ratgeber für die Schweiz | PH", description: "Tipps und Anleitungen: Genehmigungen, Steuern, Versicherungen und Leben in der Schweiz." },
    en: { title: "Blog | Guides for Life in Switzerland | PH", description: "Expert tips and guides: permits, taxes, housing, employment and insurance in Switzerland." },
    fr: { title: "Blog | Guides pour la Suisse | PH", description: "Conseils et guides : permis, impôts, logement, emploi et assurances en Suisse." },
    it: { title: "Blog | Guide per la Svizzera | PH", description: "Consigli e guide: permessi, tasse, alloggio, lavoro e assicurazioni in Svizzera." },
    pt: { title: "Blog | Guias para a Suíça | PH", description: "Dicas e guias: autorizações, impostos, habitação, emprego e seguros na Suíça." },
  },
  "/zonas": {
    de: { title: "Standorte | Punto Hispano", description: "Verwaltung, Rechtsberatung, Versicherungen und mehr weltweit. Online-Betreuung aus Opfikon, Schweiz." },
    en: { title: "Service Zones | Punto Hispano", description: "Administration, legal advice, insurance and more worldwide. Online support from Opfikon, Switzerland." },
    fr: { title: "Zones de Service | Punto Hispano", description: "Gestion, conseil juridique, assurances et plus dans le monde. Service en ligne depuis Opfikon, Suisse." },
    it: { title: "Zone di Servizio | Punto Hispano", description: "Gestione, consulenza legale, assicurazioni e altro nel mondo. Servizio online da Opfikon, Svizzera." },
    pt: { title: "Zonas de Serviço | Punto Hispano", description: "Gestão, assessoria jurídica, seguros e mais no mundo. Serviço online desde Opfikon, Suíça." },
  },
};

// Utility functions
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3).trim() + '...';
};

export const generateCanonicalUrl = (path: string): string => {
  const cleanPath = path.split('?')[0].split('#')[0];
  const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  const finalPath = normalizedPath === '/' ? '/' : normalizedPath.replace(/\/$/, '');
  return `${SEO_BASE_URL}${finalPath}`;
};

export const getSEOConfig = (path: string, locale?: string) => {
  // Strip locale prefix to find base path
  const basePath = path.replace(/^\/(es|de|fr|it|pt|en)(\/|$)/, '/').replace(/\/+$/, '') || '/';
  const config = PAGE_SEO_CONFIG[basePath];

  // Check for localized override
  const i18nOverride = locale && locale !== 'es' ? PAGE_SEO_I18N[basePath]?.[locale] : null;

  // Always derive canonical from the actual locale-prefixed path so DE/EN/FR/IT/PT
  // variants do NOT collapse onto the Spanish canonical (would break hreflang
  // and create duplicate-content signals across languages).
  const canonical = generateCanonicalUrl(path);

  if (config) {
    return {
      ...config,
      title: truncateText(i18nOverride?.title || config.title, 60),
      description: truncateText(i18nOverride?.description || config.description, 155),
      keywords: i18nOverride?.keywords || config.keywords,
      canonical,
    };
  }

  return {
    title: truncateText(i18nOverride?.title || SEO_DEFAULTS.title, 60),
    description: truncateText(i18nOverride?.description || SEO_DEFAULTS.description, 155),
    canonical,
    keywords: i18nOverride?.keywords || SEO_DEFAULTS.keywords,
    ogType: "website" as const,
  };
};